import { LatLng, MapMarker } from "../MapView.types";

// Matches legacy `components/Map/container/{google,osm}`'s single-marker
// bounding-box padding exactly, so a lone marker gets the same zoom level
// as before rather than fitting to a point (infinite zoom).
const SINGLE_MARKER_PAD = 0.002;

/**
 * The camera-management half of the map's bounds logic (the other half is
 * each adapter's own `fitBounds` call, which can't be shared — Leaflet and
 * Google Maps have different APIs for it). Identifies *which* markers are
 * currently on the map, deliberately ignoring their positions: a driver
 * update changes `driver`'s position on every poll but never adds or
 * removes a marker, so it produces the exact same signature and never
 * re-triggers a fit. The signature only changes on events that genuinely
 * warrant re-framing the camera — the initial route loading in, or the
 * driver marker appearing for the first time — matching "fit bounds on
 * load and when new required markers appear, never on a live position
 * update" from the map camera brief. Each adapter's bounds effect keys off
 * this string instead of the `markers` array itself.
 */
export function boundsSignature(markers: MapMarker[]): string {
  return markers
    .map((marker) => marker.id)
    .sort()
    .join("|");
}

/**
 * Provider-agnostic bounds math: given the positions currently on the map,
 * returns the set of points a provider's native "fit bounds" API should fit
 * to. A single point has no bounds to speak of, so — mirroring the legacy
 * Google/OSM containers' padding trick — it's inflated into a small box
 * around itself instead of zooming a single-marker map all the way in.
 */
export function computeBoundsPoints(positions: LatLng[]): LatLng[] {
  if (positions.length === 0) return [];

  if (positions.length === 1) {
    const [{ lat, lng }] = positions;
    return [
      { lat: lat + SINGLE_MARKER_PAD, lng: lng + SINGLE_MARKER_PAD },
      { lat: lat - SINGLE_MARKER_PAD, lng: lng - SINGLE_MARKER_PAD },
    ];
  }

  return positions;
}

/**
 * Which markers the *initial* camera fit/center (`center`/`initialCenter`
 * prop plus the load-time `fitBounds`) should consider — as opposed to
 * `markers`, which is always the full set actually rendered on the map.
 * In Single Drop-off Tracking mode the Pickup pin, if present, must never be
 * what the map opens centered on — only the tracked dropoff (and the driver,
 * once it appears) should drive that first fit. Outside dropoff-tracking
 * mode this is just `markers` unchanged.
 */
export function selectCameraMarkers(markers: MapMarker[], isDropoffTracking: boolean): MapMarker[] {
  if (!isDropoffTracking) return markers;
  return markers.filter((marker) => marker.kind !== "pickup");
}

/**
 * The zoom level Google's `map.fitBounds()` would settle on for `points`
 * inside a `containerSize`-pixel viewport — i.e. the same problem
 * `fitBounds` solves, reimplemented as pure math (the standard Web-Mercator
 * projection formula the Maps JS API itself uses) so a caller can *animate*
 * toward that target — `panTo` + `setZoom` — instead of jumping there via
 * `fitBounds` itself, which has no animation of its own. Only Google needs
 * this: Leaflet exposes the equivalent natively via `map.getBoundsZoom()`/
 * `map.flyToBounds()`, so `OsmMapAdapter` never calls this.
 */
export function computeFitZoom(
  points: LatLng[],
  containerSize: { width: number; height: number },
  padding = 0
): number {
  const WORLD_DIM = 256;
  const ZOOM_MAX = 21;

  if (points.length === 0) return ZOOM_MAX;

  const latRad = (lat: number): number => {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  };

  const zoomFor = (containerPx: number, fraction: number): number =>
    Math.floor(Math.log(containerPx / WORLD_DIM / fraction) / Math.LN2);

  const lats = points.map((point) => point.lat);
  const lngs = points.map((point) => point.lng);
  const north = Math.max(...lats);
  const south = Math.min(...lats);
  const west = Math.min(...lngs);
  const east = Math.max(...lngs);

  const latFraction = (latRad(north) - latRad(south)) / Math.PI || Number.EPSILON;
  const lngDiff = east - west;
  const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360 || Number.EPSILON;

  const width = Math.max(containerSize.width - padding * 2, 1);
  const height = Math.max(containerSize.height - padding * 2, 1);

  const latZoom = zoomFor(height, latFraction);
  const lngZoom = zoomFor(width, lngFraction);

  return Math.max(0, Math.min(latZoom, lngZoom, ZOOM_MAX));
}
